import { DDS_MITIGATION_SOURCE_TYPE, normalizeMitigationSourceType } from './ddsMitigationSourceType'
import { isIndonesianClient, decodeHtmlEntities } from '@/utils'
import { getProductionPlaceEudrStatus, getBaselineFarmTrafficLight, isEudrZeroNegligibleStatus } from '@/utils/trafficLightCalculator'
import { isEudrRiskDetectedStatus } from '@/utils/ddsProductionPlaceStatus'

export const MITIGATION_NOTIFICATION_TYPES = [
  'dds_mitigation_required',
  'dds_mitigation_revision',
]

export const MITIGATION_NOTIFICATION_TARGET_ROLE = 'supplier_owner'

export function decodeNotificationMessage(message) {
  if (!message || typeof message !== 'string') return message || ''
  return decodeHtmlEntities(message)
}

/** Compliance Status Review step in CreateDueDiligenceReport (TRAFFIC_LIGHT_COMPLIANCE). */
export const DDS_COMPLIANCE_TRAFFIC_LIGHT_STEP = 3

/** EUDR deforestation assessment base step in CreateDueDiligenceReport (before offsets). */
export const DDS_EUDR_ASSESSMENT_STEP = 5

function getEudrSettings(store = null) {
  const fromStore = store?.getters?.['eudrSettings/get_EUDR_Settings']
  if (fromStore) return fromStore
  try {
    return JSON.parse(localStorage.getItem('EUDR_Settings') || 'null')
  } catch {
    return null
  }
}

function isLocalDBVerificationEnabled(store = null) {
  const settings = getEudrSettings(store)
  if (!settings?.local_db_verification_config) return false
  try {
    const parsed = JSON.parse(settings.local_db_verification_config)
    return Boolean(parsed?.enabled)
  } catch {
    return false
  }
}

/**
 * EUDR assessment step — mirrors CreateDueDiligenceReport.eudrStep.
 *
 * | Client   | Local DB enabled | Step |
 * |----------|------------------|------|
 * | PTSI     | No               | 5    |
 * | PTSI     | Yes              | 6    |
 * | Non-PTSI | No               | 4    |
 * | Non-PTSI | Yes              | 5    |
 */
export function resolveDdsEudrAssessmentStep(store = null) {
  const localDbEnabled = isLocalDBVerificationEnabled(store)

  if (isIndonesianClient()) {
    return DDS_EUDR_ASSESSMENT_STEP + (localDbEnabled ? 1 : 0)
  }

  // Non-PTSI omits the traffic-light step, so EUDR is one step earlier (4 without Local DB).
  return (DDS_EUDR_ASSESSMENT_STEP - 1) + (localDbEnabled ? 1 : 0)
}

export function throwOnGraphqlErrors(response, fallbackMessage = 'Request failed') {
  if (!response?.errors?.length) return response
  const message = response.errors[0]?.message
    || response.errors[0]?.extensions?.response?.message
    || fallbackMessage
  throw new Error(message)
}

export function getReportProductionPlaceId(productionPlace) {
  const reportPlaceId = productionPlace?.diligenceReportProductionPlaceArray?.[0]?.id
  if (reportPlaceId != null) return parseInt(reportPlaceId, 10)
  const fallbackId = productionPlace?.id
  return fallbackId != null ? parseInt(fallbackId, 10) : null
}

export function getProductionPlaceDeforestationStatus(productionPlace) {
  return productionPlace?.deforestationStatus
    || productionPlace?.deforestationInfo?.deforestationStatus
    || productionPlace?.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo?.deforestationStatus
    || productionPlace?.eudr_deforestation_status
    || 'Medium Deforestation Probability'
}

const REVISION_REQUESTED_PREFIX = 'revision requested:'
const UPLOAD_REQUESTED_PREFIX = 'upload requested:'
const PENDING_FARM_REVISION_STORAGE_PREFIX = 'dds_pending_farm_revisions:'

export function getMitigationRevisionComment(productionPlace) {
  const candidates = [
    productionPlace?.risk_mitigation_comment,
    productionPlace?.deforestationInfo?.deforestationMitigationComment,
    productionPlace?.productionPlaceDeforestationInfo?.deforestationMitigationComment,
    productionPlace?.diligenceReportProductionPlaceArray?.[0]?.productionPlaceDeforestationInfo?.deforestationMitigationComment,
  ]

  for (const raw of candidates) {
    if (typeof raw === 'string' && raw.trim()) return raw.trim()
  }

  return ''
}

/** True when PTSI requested revision and cooperator has not resubmitted yet. */
export function isMitigationRevisionRequested(productionPlace) {
  return getMitigationRevisionComment(productionPlace).toLowerCase().startsWith(REVISION_REQUESTED_PREFIX)
}

/** True when PTSI requested mitigation upload from cooperator. */
export function isMitigationUploadRequested(productionPlace) {
  return getMitigationRevisionComment(productionPlace).toLowerCase().startsWith(UPLOAD_REQUESTED_PREFIX)
}

export function hasPendingMitigationBatchComment(productionPlace) {
  const comment = getMitigationRevisionComment(productionPlace).toLowerCase()
  return comment.startsWith(REVISION_REQUESTED_PREFIX) || comment.startsWith(UPLOAD_REQUESTED_PREFIX)
}

export function parseMitigationBatchComment(productionPlace) {
  const full = getMitigationRevisionComment(productionPlace)
  if (!full) return null

  const lower = full.toLowerCase()
  if (lower.startsWith(REVISION_REQUESTED_PREFIX)) {
    return {
      requestType: 'revision',
      comment: full.slice(full.toLowerCase().indexOf(REVISION_REQUESTED_PREFIX) + REVISION_REQUESTED_PREFIX.length).trim(),
    }
  }
  if (lower.startsWith(UPLOAD_REQUESTED_PREFIX)) {
    return {
      requestType: 'upload_required',
      comment: full.slice(full.toLowerCase().indexOf(UPLOAD_REQUESTED_PREFIX) + UPLOAD_REQUESTED_PREFIX.length).trim(),
    }
  }
  return null
}

export function buildFarmRevisionRequestFromPendingComment(productionPlace) {
  const parsed = parseMitigationBatchComment(productionPlace)
  if (!parsed) return null
  return buildFarmRevisionRequestEntry(productionPlace, parsed)
}

function normalizeFarmRevisionRequests(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

export function formatFarmRevisionRequestsForDisplay(farms = []) {
  const list = normalizeFarmRevisionRequests(farms)
  if (!list.length) return { count: 0, text: '', lines: [] }

  const lines = list.map((item, index) => {
    const farmName = item?.farmName || `Farm ${item?.farmId || index + 1}`
    const regId = item?.farmerRegId || item?.farmRegId || ''
    const comment = item?.comment || ''
    const requestType = item?.requestType === 'revision' ? 'Revision required' : 'Upload required'
    const regPart = regId ? ` (${regId})` : ''
    const commentPart = comment ? ` — ${comment}` : ''
    return `${index + 1}. ${farmName}${regPart} [${requestType}]${commentPart}`
  })

  return {
    count: list.length,
    text: lines.join('\n'),
    lines,
  }
}

export function getDdrAdditionalInformationNotificationFarms(notification) {
  const data = parseNotificationData(notification)
  const farms = normalizeFarmRevisionRequests(data.farmRevisionRequests)
  if (farms.length) return farms

  const summaryText = data.farmRevisionSummaryText
  if (typeof summaryText === 'string' && summaryText.trim()) {
    return summaryText.split('\n').map((line, index) => ({
      farmName: line.replace(/^\d+\.\s*/, '').trim() || `Farm ${index + 1}`,
      comment: '',
      requestType: line.toLowerCase().includes('revision required') ? 'revision' : 'upload_required',
    }))
  }

  return []
}

export function formatDdrAdditionalInformationNotificationMessage(notification) {
  const baseMessage = decodeNotificationMessage(notification?.message || '')
  const farms = getDdrAdditionalInformationNotificationFarms(notification)
  const summary = formatFarmRevisionRequestsForDisplay(farms)

  if (!summary.count) {
    return baseMessage
  }

  if (baseMessage.includes(summary.lines[0]?.split(' [')[0] || '__no_match__')) {
    return baseMessage
  }

  const intro = baseMessage.split('\n')[0] || baseMessage
  return `${intro}\n\n${summary.count} production place(s) requiring updates:\n${summary.text}`
}

export function getDdrAdditionalInformationNotificationIntro(notification) {
  const message = decodeNotificationMessage(notification?.message || '')
  const farms = getDdrAdditionalInformationNotificationFarms(notification)

  if (!farms.length) {
    return message
  }

  const introLines = message
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !/^\d+\.\s/.test(line))

  if (introLines.length) {
    return introLines.join(' ')
  }

  return 'Additional information has been requested for your Due Diligence Report.'
}

/** Compact summary for the AppBar notification dropdown (no per-farm list). */
export function getDdrAdditionalInformationAppBarIntro(notification) {
  const data = parseNotificationData(notification)
  const farms = getDdrAdditionalInformationNotificationFarms(notification)
  const count = farms.length || parseInt(data.farmRevisionCount, 10) || 0
  const message = decodeNotificationMessage(notification?.message || '')
  const baseMatch = message.match(/^(.+?Due Diligence Report\.)/i)
  const base = baseMatch?.[1] || 'Additional information has been requested for your Due Diligence Report.'

  if (count > 0) {
    return `${base} ${count} production place(s) require mitigation updates.`
  }

  return base
}

export function getDdrAdditionalInformationNotificationTitle(notification) {
  const data = parseNotificationData(notification)
  const count = data.farmRevisionCount
    || normalizeFarmRevisionRequests(data.farmRevisionRequests).length
    || 0
  if (count > 0) {
    return `Additional Information Requested (${count} farms)`
  }
  return notification?.title || 'Additional Information Requested'
}

export function getFarmRevisionQueueKey(entry) {
  if (entry?.productionPlaceId) return `pp:${entry.productionPlaceId}`
  if (entry?.farmId) return `farm:${entry.farmId}`
  return null
}

export function collectNotifiedFarmRevisionKeys(requestAdditionalInformation = []) {
  const keys = new Set()
  for (const request of requestAdditionalInformation) {
    const farms = normalizeFarmRevisionRequests(request?.farmRevisionRequests)
    for (const farm of farms) {
      const entry = {
        productionPlaceId: farm.productionPlaceId,
        farmId: farm.farmId,
      }
      const key = getFarmRevisionQueueKey(entry)
      if (key) keys.add(key)
    }
  }
  return keys
}

export function isFarmRevisionAlreadyNotified(entry, notifiedKeys) {
  if (!entry || !notifiedKeys?.size) return false
  const key = getFarmRevisionQueueKey(entry)
  return key ? notifiedKeys.has(key) : false
}

export function mergeFarmRevisionRequestEntries(...lists) {
  const map = new Map()
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const entry of list) {
      if (!entry) continue
      const key = getFarmRevisionQueueKey(entry)
      if (!key) continue
      map.set(key, { ...map.get(key), ...entry })
    }
  }
  return [...map.values()]
}

export function getPendingFarmRevisionStorageKey(diligenceId) {
  const id = parseInt(diligenceId, 10)
  return Number.isNaN(id) ? null : `${PENDING_FARM_REVISION_STORAGE_PREFIX}${id}`
}

export function loadPendingFarmRevisionQueue(diligenceId) {
  if (typeof localStorage === 'undefined') return []
  const key = getPendingFarmRevisionStorageKey(diligenceId)
  if (!key) return []
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function savePendingFarmRevisionQueue(diligenceId, entries = []) {
  if (typeof localStorage === 'undefined') return
  const key = getPendingFarmRevisionStorageKey(diligenceId)
  if (!key) return
  if (!entries.length) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, JSON.stringify(entries))
}

export function clearPendingFarmRevisionQueue(diligenceId) {
  savePendingFarmRevisionQueue(diligenceId, [])
}

export function restorePendingFarmRevisionQueue({
  diligenceId,
  productionPlaces = [],
  requestAdditionalInformation = [],
} = {}) {
  const notifiedKeys = collectNotifiedFarmRevisionKeys(requestAdditionalInformation)

  const fromStorage = loadPendingFarmRevisionQueue(diligenceId)
    .filter((entry) => !isFarmRevisionAlreadyNotified(entry, notifiedKeys))

  const fromApi = (productionPlaces || [])
    .map((place) => buildFarmRevisionRequestFromPendingComment(place))
    .filter(Boolean)
    .filter((entry) => !isFarmRevisionAlreadyNotified(entry, notifiedKeys))

  return mergeFarmRevisionRequestEntries(fromStorage, fromApi)
}

export function parseNotificationData(notification) {
  const raw = notification?.data
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}

export function isMitigationNotification(notification) {
  return MITIGATION_NOTIFICATION_TYPES.includes(notification?.type)
}

export function isMitigationRevisionNotification(notification) {
  return notification?.type === 'dds_mitigation_revision'
}

export function getMitigationReportId(notification) {
  const data = parseNotificationData(notification)
  const id = data.ddrId || data.dueDiligenceReportId
  return id != null ? parseInt(id, 10) : null
}

export function getMitigationFarmId(notification) {
  const data = parseNotificationData(notification)
  const id = data.farmId ?? data.cfFarmId
  if (id == null) return null
  const parsed = parseInt(id, 10)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Farm id for updateManualEUDRDeforestationStatus — must match due_diligence_production_places.farmId,
 * not farms.cf_farmid (often exposed incorrectly as farm.id in federated GraphQL).
 */
export function getDdsUpdateFarmId(productionPlace) {
  if (!productionPlace) return null

  const farmId = productionPlace.farmId
  if (farmId != null) {
    const parsed = parseInt(farmId, 10)
    if (!Number.isNaN(parsed) && parsed > 0) return parsed
  }

  const nestedFarmId = productionPlace.farm?.id
  if (nestedFarmId != null) {
    const parsed = parseInt(nestedFarmId, 10)
    if (!Number.isNaN(parsed) && parsed > 0) return parsed
  }

  return null
}

/**
 * Target EUDR deforestation status when PTSI approves mitigation evidence.
 * Local DB medium/high risk or EUDR risk detected → Manually Mitigated; fully compliant → unchanged.
 */
export function resolveMitigationApprovalStatus(productionPlace) {
  const currentStatus = getProductionPlaceEudrStatus(productionPlace)
  if (!currentStatus) return 'Manually Mitigated'

  if (isEudrZeroNegligibleStatus(currentStatus)) return currentStatus

  const baselineTl = getBaselineFarmTrafficLight(productionPlace)
  if (baselineTl === 'YELLOW' || baselineTl === 'LIGHT_RED' || baselineTl === 'DARK_RED') {
    return 'Manually Mitigated'
  }

  const normalized = String(currentStatus).toLowerCase()
  if (normalized.includes('manually mitigated')) return 'Manually Mitigated'

  if (isEudrZeroNegligibleStatus(currentStatus) || normalized === 'compliant') {
    return currentStatus
  }

  if (isEudrRiskDetectedStatus(currentStatus)) {
    return 'Manually Mitigated'
  }

  return 'Manually Mitigated'
}

export function getMitigationDeepLinkCriteria(notification) {
  const data = parseNotificationData(notification)
  const toInt = (value) => {
    if (value == null) return null
    const parsed = parseInt(value, 10)
    return Number.isNaN(parsed) ? null : parsed
  }

  return {
    farmId: getMitigationFarmId(notification),
    productionPlaceId: toInt(data.productionPlaceId),
    diligenceReportProductionPlaceId: toInt(data.diligenceReportProductionPlaceId),
    revisionComment: data.revisionComment || '',
  }
}

export function findProductionPlaceForMitigation(places, criteria = {}) {
  if (!Array.isArray(places) || !places.length) return null

  const { farmId, productionPlaceId, diligenceReportProductionPlaceId } = criteria

  if (diligenceReportProductionPlaceId) {
    const match = places.find((row) => (
      parseInt(row.diligenceReportProductionPlaceArray?.[0]?.id, 10) === diligenceReportProductionPlaceId
    ))
    if (match) return match
  }

  if (productionPlaceId) {
    const match = places.find((row) => parseInt(row.id, 10) === productionPlaceId)
    if (match) return match
  }

  if (farmId) {
    const match = places.find((row) => {
      const rowFarmId = parseInt(row.farmId, 10)
      const nestedFarmId = parseInt(row.farm?.id, 10)
      const cfFarmId = parseInt(row.farm?.cf_farmid, 10)
      return rowFarmId === farmId || nestedFarmId === farmId || cfFarmId === farmId
    })
    if (match) return match
  }

  return null
}

export function buildMitigationResubmitRoute(notification) {
  const ddsId = getMitigationReportId(notification)
  if (!ddsId) {
    return { name: 'dds_root_due_diligence_report' }
  }

  const { farmId, productionPlaceId, diligenceReportProductionPlaceId, revisionComment } = (
    getMitigationDeepLinkCriteria(notification)
  )

  const query = { openMitigation: '1' }
  if (farmId) query.mitigationFarmId = String(farmId)
  if (productionPlaceId) query.mitigationProductionPlaceId = String(productionPlaceId)
  if (diligenceReportProductionPlaceId) query.mitigationDrppId = String(diligenceReportProductionPlaceId)
  if (revisionComment) query.mitigationRevisionComment = revisionComment

  return {
    name: 'createDueDiligenceReports',
    params: { id: String(ddsId) },
    query,
  }
}

export async function navigateToMitigationResubmit({ router, store, notification }) {
  const route = buildMitigationResubmitRoute(notification)

  if (route.name !== 'createDueDiligenceReports') {
    await router.push(route).catch(() => {})
    return
  }

  await store.dispatch('SET_DDS_TAB', { activeTab: DDS_COMPLIANCE_TRAFFIC_LIGHT_STEP })
  await router.push(route).catch(() => {})
  window.location.reload()
}

export function resolveFarmRevisionMitigationSourceType(farm = {}) {
  return normalizeMitigationSourceType(farm.mitigationSourceType)
    || normalizeMitigationSourceType(farm.mitigation_source_type)
    || DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE
}

export function resolveDdsStepForFarmRevisionRequest(farm = {}, store = null) {
  const source = resolveFarmRevisionMitigationSourceType(farm)
  if (source === DDS_MITIGATION_SOURCE_TYPE.EUDR_DEFORESTATION) {
    return resolveDdsEudrAssessmentStep(store)
  }
  return DDS_COMPLIANCE_TRAFFIC_LIGHT_STEP
}

export function buildDdrAdditionalInformationFarmRoute(notification, farm = {}) {
  const data = parseNotificationData(notification)
  const ddsId = data.ddrId || data.dueDiligenceReportId
  if (!ddsId) {
    return { name: 'dds_root_due_diligence_report' }
  }

  const source = resolveFarmRevisionMitigationSourceType(farm)
  const query = {
    openMitigation: '1',
    mitigationContext: source,
  }

  if (data.dueDiligenceRequestAdditionalInformationId) {
    query.ddrAdditionalInformationId = String(data.dueDiligenceRequestAdditionalInformationId)
  }
  if (farm.farmId) query.mitigationFarmId = String(farm.farmId)
  if (farm.productionPlaceId) query.mitigationProductionPlaceId = String(farm.productionPlaceId)
  if (farm.comment) query.mitigationRevisionComment = farm.comment

  return {
    name: 'createDueDiligenceReports',
    params: { id: String(ddsId) },
    query,
  }
}

export async function navigateToDdrAdditionalInformationFarm({
  router,
  store,
  notification,
  farm,
}) {
  const data = parseNotificationData(notification)
  const route = buildDdrAdditionalInformationFarmRoute(notification, farm)

  if (store?.dispatch) {
    await store.dispatch('eudrSettings/loadEudrSetting').catch(() => {})
  }

  const step = resolveDdsStepForFarmRevisionRequest(farm, store)

  if (data.dueDiligenceRequestAdditionalInformationId) {
    await store.dispatch('eudrDDS/setDdsAdditionalInformationData', {
      ddrId: data.ddrId || data.dueDiligenceReportId,
      ddrAdditionalInformationId: data.dueDiligenceRequestAdditionalInformationId,
    }).catch(() => {})
  }

  if (route.name !== 'createDueDiligenceReports') {
    await router.push(route).catch(() => {})
    return
  }

  await store.dispatch('SET_DDS_TAB', { activeTab: step })
  await router.push(route).catch(() => {})
  window.location.reload()
}

export function parseDdsUserId(value) {
  if (value == null || value === '') return null
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed
}

/**
 * Farmer-service User.id for the cooperator/supplier to notify.
 * PTSI approval reports may only have userId when supplierId is unset.
 */
export function resolveDdsSupplierUserId(report = null) {
  if (!report) return null

  const candidates = [
    report.supplier?.id,
    report.supplierId,
    report.user?.id,
    report.userId,
  ]

  for (const value of candidates) {
    const id = parseDdsUserId(value)
    if (id) return id
  }

  const requests = report.requestAdditionalInformation
  if (Array.isArray(requests) && requests.length) {
    const sorted = [...requests].sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime()
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime()
      return bTime - aTime
    })
    for (const request of sorted) {
      const id = parseDdsUserId(request.supplierId)
      if (id) return id
    }
  }

  return null
}

export function resolveDdsSupplierCfUserId(report = null) {
  if (!report) return null
  return report.supplier?.cf_userid
    || report.user?.cf_userid
    || report.supplierCfUserId
    || null
}

export function buildFarmRevisionRequestEntry(productionPlace, {
  comment = '',
  requestType = 'upload_required',
  mitigationSourceType = DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE,
} = {}) {
  const farm = productionPlace?.farm || {}
  const normalizedSource = normalizeMitigationSourceType(mitigationSourceType)
    || DDS_MITIGATION_SOURCE_TYPE.LOCAL_DB_COMPLIANCE
  return {
    productionPlaceId: productionPlace?.id ? parseInt(productionPlace.id, 10) : null,
    farmId: getDdsUpdateFarmId(productionPlace),
    farmName: farm.farmName || `Farm ${farm.id || productionPlace?.id || ''}`,
    farmerRegId: farm.farmerRegId || farm.nationalFarmId || String(farm.id || ''),
    address: farm.address || '',
    comment: (comment || '').trim(),
    requestType,
    mitigationSourceType: normalizedSource,
  }
}

export function buildMitigationRevisionNotification({
  reportId,
  reportRefNumber,
  farm,
  comment,
  userIds,
  organizationId = null,
}) {
  const farmName = farm?.farm?.farmName || farm?.farm?.id || 'farm'
  const ref = reportRefNumber || String(reportId)
  const reportPlaceId = farm?.diligenceReportProductionPlaceArray?.[0]?.id || null
  const orgId = parseCfUserId(organizationId)

  return {
    title: 'Mitigation Revision Required',
    type: 'dds_mitigation_revision',
    notify: 'user',
    message: `Your mitigation evidence for farm "${farmName}" (DDS Ref: ${ref}) needs revision. Reason: ${comment}. Please log in and resubmit updated documents.`,
    users: userIds,
    data: JSON.stringify({
      ddrId: parseInt(reportId, 10),
      dueDiligenceReportId: parseInt(reportId, 10),
      farmId: getDdsUpdateFarmId(farm) || (farm?.farm?.id ? parseInt(farm.farm.id, 10) : null),
      productionPlaceId: farm?.id ? parseInt(farm.id, 10) : null,
      diligenceReportProductionPlaceId: reportPlaceId,
      revisionComment: comment,
      organizationId: orgId,
      targetRole: MITIGATION_NOTIFICATION_TARGET_ROLE,
    }),
  }
}

export function parseCfUserId(value) {
  if (value == null || value === '') return null
  const parsed = parseInt(value, 10)
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed
}

/**
 * CF platform user ids for mitigation revision — never DDS / farmer-service user ids.
 */
export function resolveMitigationRevisionRecipientIds({
  report = null,
  supplierCfUserId = null,
} = {}) {
  const ids = new Set()
  const add = (value) => {
    const id = parseCfUserId(value)
    if (id) ids.add(id)
  }

  add(supplierCfUserId)
  if (report) {
    add(report.supplierCfUserId)
    add(report.supplier?.cf_userid)
    add(report.user?.cf_userid)
  }

  return [...ids]
}

export function getMitigationNotificationViewerContext(user = null) {
  let parsedUser = user
  if (!parsedUser) {
    try {
      parsedUser = JSON.parse(localStorage.getItem('user') || 'null')
    } catch {
      parsedUser = null
    }
  }

  const roles = (parsedUser?.user_role_assoc || []).map((role) => role.id)
  const organizationId = parsedUser?.organization
    ?? parsedUser?.user_organization?.id
    ?? null

  return { roles, organizationId }
}

export function canViewMitigationNotification(notification, viewerContext = {}) {
  if (!isMitigationNotification(notification)) return true

  const roles = viewerContext.roles || []
  if (!roles.includes(MITIGATION_NOTIFICATION_TARGET_ROLE)) return false

  const data = parseNotificationData(notification)
  const targetOrgId = parseCfUserId(data.organizationId)
  if (!targetOrgId) return true

  const viewerOrgId = parseCfUserId(viewerContext.organizationId)
  if (!viewerOrgId) return false

  return viewerOrgId === targetOrgId
}

export function filterMitigationNotificationsForViewer(notifications = [], viewerContext = null) {
  const context = viewerContext || getMitigationNotificationViewerContext()
  return notifications.filter((item) => canViewMitigationNotification(item, context))
}

export function countUnreadMitigationNotifications(notifications = [], viewerContext = null) {
  const visible = filterMitigationNotificationsForViewer(notifications, viewerContext)
  return visible.filter((item) => item?.notificationTo?.seen === '0' || item?.notificationTo?.seen === 0).length
}
